(() => {
  if (window.__bzPdpExtras) return;
  window.__bzPdpExtras = true;

  const desktop = window.matchMedia('(min-width: 980px)');
  const mainRoot = () => document.querySelector("product-info[id^='MainProduct-']");
  const BADGE = ".product__info-container > [id^='price-'] .bezelor-pdp-saving";

  /* ---------- helpers ---------- */
  function readSelectedVariant(root) {
    try {
      return JSON.parse(root.querySelector('variant-selects [data-selected-variant]').textContent);
    } catch (error) {
      return null;
    }
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /* ---------- 1. Variant -> gallery image (desktop) ---------- */
  const stripSrc = (src) =>
    String(src || '').split('?')[0].replace(/^https?:/, '').replace(/_\d+x\d*(?=\.\w+$)/, '');

  function mediaIdFor(root, variant) {
    if (variant.featured_media && variant.featured_media.id) return root.dataset.section + '-' + variant.featured_media.id;
    const src = variant.featured_image && variant.featured_image.src;
    if (!src) return null;
    const wanted = stripSrc(src);
    for (const item of root.querySelectorAll('media-gallery li[data-media-id]')) {
      const img = item.querySelector('img');
      if (img && stripSrc(img.currentSrc || img.src) === wanted) return item.dataset.mediaId;
    }
    return null;
  }

  /* ---------- 2. Sale banner above the price (desktop) ---------- */
  function percentFor(variant) {
    if (!variant || !(variant.compare_at_price > variant.price)) return 0;
    return Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
  }

  function renderBanner(root, variant) {
    const price = root.querySelector(".product__info-container > [id^='price-']");
    if (!price) return;
    let banner = root.querySelector('.bz-sale-banner');
    if (!banner) {
      banner = el('div', 'bz-sale-banner');
      banner.hidden = true;
      banner.append(el('strong', '', 'Sale'), el('span', 'bz-sale-banner-text'));
      price.before(banner);
    }
    const percent = percentFor(variant);
    banner.hidden = !percent;
    banner.querySelector('.bz-sale-banner-text').textContent = percent ? '· ' + percent + '% off' : '';
  }

  /* ---------- 3. Buy box column (desktop): quantity + Add to cart on one line ---------- */
  function ensureBuyBox(root) {
    const grid = root.querySelector('.product');
    if (!grid) return null;
    let wrapper = grid.querySelector(':scope > .bz-buybox-wrapper');
    if (!wrapper) {
      const template = root.querySelector('template[data-bz-buybox]');
      if (!template || !template.content.firstElementChild) return null;
      wrapper = el('div', 'grid__item bz-buybox-wrapper');
      wrapper.append(template.content.firstElementChild.cloneNode(true));
      grid.append(wrapper);
      grid.classList.add('bz-has-buybox');
    }
    return wrapper;
  }

  function fillBuyBox(root) {
    const wrapper = ensureBuyBox(root);
    if (!wrapper || !desktop.matches) return;
    const formSlot = wrapper.querySelector("[data-bz-slot='form']");
    const form = root.querySelector('product-form');
    if (form && form.parentElement !== formSlot) formSlot.append(form);
    const quantity = root.querySelector('.product-form__quantity');
    const buttons = form && form.querySelector('.product-form__buttons');
    if (quantity && buttons && quantity.parentElement !== buttons) buttons.prepend(quantity);
    wrapper.classList.toggle('bz-buybox--qty', !!(quantity && buttons));
  }

  /* ---------- 3b. Perk tiles sit under the main image on desktop ---------- */
  function placePerks(root) {
    const perks = root.querySelector('[data-bz-perks]');
    if (!perks) return;
    if (!perks._bzHome) {
      perks._bzHome = document.createComment('bz-perks-home');
      perks.before(perks._bzHome);
    }
    const wrapper = root.querySelector('.product__media-wrapper');
    if (desktop.matches && wrapper) {
      if (perks.parentElement !== wrapper) wrapper.append(perks);
    } else if (perks._bzHome.parentNode && perks._bzHome.nextSibling !== perks) {
      perks._bzHome.after(perks);
    }
  }

  /* ---------- 4. Discounts & coupon codes popup (from the discount badge) ---------- */
  function parseCoupon(title) {
    const text = String(title || '');
    const pct = /(\d+(?:\.\d+)?)\s*%/.exec(text);
    const amount = /(\d+(?:\.\d+)?)/.exec(text);
    const min = /(?:orders?|over|spend|min\w*)\D*?(\d+(?:\.\d+)?)/i.exec(text);
    return {
      percent: pct ? parseFloat(pct[1]) : 0,
      amount: pct ? 0 : amount ? parseFloat(amount[1]) : 0,
      min: min ? parseFloat(min[1]) : 0,
    };
  }

  function renderDiscounts(root, dialog) {
    const variant = readSelectedVariant(root);
    if (!variant) return;
    const qty = Math.max(1, parseInt(root.querySelector('.quantity__input')?.value, 10) || 1);
    const currency = dialog.dataset.currency || 'GBP';
    const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    const subtotal = variant.price * qty;
    const original = Math.max(variant.compare_at_price || 0, variant.price) * qty;

    const coupons = [...dialog.querySelectorAll('[data-bz-disc-source] li')]
      .map((li) => ({ code: li.dataset.code, title: li.dataset.title, ...parseCoupon(li.dataset.title) }))
      .filter((c) => c.code)
      .map((c) => {
        const minCents = Math.round(c.min * 100 * rate);
        const saving = c.percent ? Math.round((subtotal * c.percent) / 100) : Math.min(subtotal, Math.round(c.amount * 100 * rate));
        return { ...c, minCents, saving, eligible: subtotal >= minCents };
      });
    const eligible = coupons.filter((c) => c.eligible).sort((a, b) => b.saving - a.saving);
    const locked = coupons.filter((c) => !c.eligible).sort((a, b) => a.minCents - b.minCents);
    const best = eligible[0];
    const next = locked[0];
    const est = subtotal - (best ? best.saving : 0);

    dialog.querySelector('[data-bz-disc-est]').textContent = money(est, currency);

    const dl = dialog.querySelector('[data-bz-disc-breakdown]');
    dl.replaceChildren();
    const row = (label, value, modifier) => {
      const line = el('div', 'bz-disc-row' + (modifier ? ' bz-disc-row--' + modifier : ''));
      line.append(el('dt', '', label), el('dd', '', value));
      dl.append(line);
    };
    const qtyNote = qty > 1 ? ' (×' + qty + ')' : '';
    if (original > subtotal) row('Original price' + qtyNote, money(original, currency), 'struck');
    row((original > subtotal ? 'Item discounted price' : 'Item price') + qtyNote, money(subtotal, currency));
    if (best) row('Store coupon code · ' + best.code, '- ' + money(best.saving, currency), 'coupon');
    else if (next) row('Add ' + money(next.minCents - subtotal, currency) + ' more to use ' + next.code, '', 'hint');
    row('Est. after benefits:', money(est, currency), 'total');

    const list = dialog.querySelector('[data-bz-disc-list]');
    list.replaceChildren();
    [...eligible, ...locked].forEach((c) => {
      const card = el('li', 'bz-disc-card' + (c.eligible ? '' : ' is-locked'));
      const main = el('div', 'bz-disc-card-main');
      const copy = el('div', 'bz-disc-card-copy');
      const amountLabel = c.percent ? c.percent + '% off' : money(Math.round(c.amount * 100 * rate), currency) + ' off';
      copy.append(el('strong', 'bz-disc-amount', amountLabel));
      copy.append(el('span', 'bz-disc-min', c.minCents ? 'Orders over ' + money(c.minCents, currency) : 'No minimum spend'));
      const side = el('div', 'bz-disc-card-side');
      const button = el('button', 'bz-disc-apply', c.eligible ? 'Apply' : 'Copy');
      button.type = 'button';
      button.dataset.bzDiscCode = c.code;
      button.dataset.bzDiscMode = c.eligible ? 'apply' : 'copy';
      side.append(button, el('span', 'bz-disc-code', c.code));
      main.append(copy, side);
      let footText = 'Store coupon code';
      if (c === best) footText += ' · Best for this item';
      else if (!c.eligible) footText += ' · Add ' + money(c.minCents - subtotal, currency) + ' more to unlock';
      else footText += ' · Saves ' + money(c.saving, currency);
      card.append(main, el('div', 'bz-disc-card-foot', footText));
      if (c === best) card.prepend(el('span', 'bz-disc-best', 'Best'));
      list.append(card);
    });
    dialog.querySelector('.bz-disc-status').textContent = '';
  }

  async function useCode(button, dialog) {
    const code = button.dataset.bzDiscCode;
    const status = dialog.querySelector('.bz-disc-status');
    const original = button.textContent;
    let copied = false;
    try {
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch (error) {}

    if (button.dataset.bzDiscMode === 'copy') {
      button.textContent = copied ? 'Copied' : original;
      status.textContent = copied
        ? code + ' copied. It applies once your order reaches the minimum spend.'
        : 'Use code ' + code + ' at checkout once your order reaches the minimum spend.';
      return;
    }

    button.disabled = true;
    button.textContent = 'Applying…';
    try {
      const response = await fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code }),
      });
      if (!response.ok) throw new Error('Cart update failed');
      const cart = await response.json();
      const same = (value) => String(value || '').toUpperCase() === code.toUpperCase();
      const applied = (cart.discount_codes || []).some((d) => same(d.code) && d.applicable === true);
      button.textContent = applied ? 'Applied' : 'Saved';
      status.textContent = applied
        ? code + ' is applied to your cart.'
        : code + ' is saved to your cart and applies when your order qualifies.';
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bezelor-discounts', cartData: cart });
      }
    } catch (error) {
      button.textContent = original;
      status.textContent = 'Could not apply ' + code + ' right now. Enter it at checkout instead.';
    } finally {
      button.disabled = false;
    }
  }

  function armBadge(root) {
    const badge = root.querySelector(BADGE);
    if (!badge || badge.dataset.bzArmed || !root.querySelector('dialog.bz-disc-dialog')) return;
    badge.dataset.bzArmed = 'true';
    badge.setAttribute('role', 'button');
    badge.tabIndex = 0;
    badge.setAttribute('aria-haspopup', 'dialog');
    badge.setAttribute('aria-label', badge.textContent.trim().replace(/\s+/g, ' ') + '. View discounts and coupon codes');
  }

  /* ---------- 5. Popups (policies + discounts) ---------- */
  const SHEETS = 'dialog.bz-policy-dialog, dialog.bz-disc-dialog';

  function policyByKey(key) {
    return document.querySelector("dialog.bz-policy-dialog[data-bz-policy='" + key + "']");
  }

  function policyByUrl(href) {
    let target;
    try {
      target = new URL(href, location.href);
    } catch (error) {
      return null;
    }
    if (target.origin !== location.origin) return null;
    for (const dialog of document.querySelectorAll('dialog.bz-policy-dialog')) {
      try {
        if (new URL(dialog.dataset.policyUrl, location.href).pathname === target.pathname) return dialog;
      } catch (error) {}
    }
    return null;
  }

  function openSheet(dialog, opener) {
    if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return false;
    dialog._bzReturn = opener || document.activeElement;
    dialog._bzOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.showModal();
    const body = dialog.querySelector('.bz-policy-body, .bz-disc-body');
    if (body) body.scrollTop = 0;
    dialog.querySelector('.bz-policy-x, .bz-disc-x')?.focus({ preventScroll: true });
    return true;
  }

  function openDiscounts(badge) {
    const root = badge.closest('product-info');
    const dialog = root && root.querySelector('dialog.bz-disc-dialog');
    if (!dialog) return false;
    renderDiscounts(root, dialog);
    return openSheet(dialog, badge);
  }

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.matches(SHEETS)) return;
      document.body.style.overflow = dialog._bzOverflow || '';
      dialog._bzReturn?.focus?.({ preventScroll: true });
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const badge = event.target.closest && event.target.closest(BADGE);
    if (badge && openDiscounts(badge)) event.preventDefault();
  });

  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-bz-policy-close], [data-bz-disc-close]');
    if (closeButton) {
      closeButton.closest('dialog')?.close();
      return;
    }

    if (event.target instanceof HTMLDialogElement && event.target.matches(SHEETS)) {
      const rect = event.target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
        event.target.close();
      }
      return;
    }

    const codeButton = event.target.closest('[data-bz-disc-code]');
    if (codeButton) {
      useCode(codeButton, codeButton.closest('dialog'));
      return;
    }

    const badge = event.target.closest(BADGE);
    if (badge) {
      openDiscounts(badge);
      return;
    }

    const policyOpener = event.target.closest('[data-bz-policy-open]');
    if (policyOpener) {
      if (openSheet(policyByKey(policyOpener.dataset.bzPolicyOpen), policyOpener)) event.preventDefault();
      return;
    }

    const brandOpener = event.target.closest('[data-bz-open-brand-plus]');
    if (brandOpener) {
      brandOpener.closest('product-info')?.querySelector('.product__title .bz-brand-plus-badge')?.click();
      return;
    }

    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.target === '_blank') return;
    if (!link.closest('product-info, .bezelor-pdp-flow') || link.closest('dialog.bz-policy-dialog')) return;
    if (openSheet(policyByUrl(link.href), link)) event.preventDefault();
  });

  /* ---------- wiring ---------- */
  function onVariantChange(payload) {
    const variant = payload && payload.data && payload.data.variant;
    const root = mainRoot();
    if (!root || !variant) return;
    renderBanner(root, variant);
    armBadge(root);
    if (!desktop.matches) return;
    const gallery = root.querySelector('media-gallery');
    const mediaId = mediaIdFor(root, variant);
    if (gallery && mediaId && typeof gallery.setActiveMedia === 'function') gallery.setActiveMedia(mediaId, false);
  }

  function setup() {
    const root = mainRoot();
    if (!root) return;
    renderBanner(root, readSelectedVariant(root));
    fillBuyBox(root);
    placePerks(root);
    armBadge(root);
    const price = root.querySelector(".product__info-container > [id^='price-']");
    if (price && !price.dataset.bzObserved) {
      price.dataset.bzObserved = 'true';
      new MutationObserver(() => armBadge(root)).observe(price, { childList: true, subtree: true });
    }
  }

  function start() {
    setup();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  desktop.addEventListener('change', () => setTimeout(setup, 0));
  document.addEventListener('product-info:loaded', () => setTimeout(setup, 0));
  document.addEventListener('shopify:section:load', () => setTimeout(setup, 0));
})();
